import React, { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { X, User, Settings, LogOut, CreditCard, Moon, Sun } from 'lucide-react'
import { useNavigate, useLocation } from 'react-router-dom'
import { useAuth } from '../hooks/useAuth'
import { setAuthReturnTo, currentPathForReturn } from '../utils/authRedirect'

interface AccountDropdownProps {
  isOpen: boolean
  onClose: () => void
  isDropdown?: boolean
  dropdownRef?: React.RefObject<HTMLDivElement>
}

export function AccountDropdown({ isOpen, onClose, isDropdown = false, dropdownRef }: AccountDropdownProps) {
  const navigate = useNavigate()
  const location = useLocation()
  const { signOut, user, admin, role } = useAuth()
  const [theme, setTheme] = useState<'light' | 'dark'>('light')

  useEffect(() => {
    const storedTheme = localStorage.getItem('theme') as 'light' | 'dark' | null
    if (storedTheme) {
      setTheme(storedTheme)
      document.documentElement.classList.toggle('dark', storedTheme === 'dark')
    } else if (window.matchMedia('(prefers-color-scheme: dark)').matches) {
      setTheme('dark')
      document.documentElement.classList.add('dark')
    }
  }, [])

  const toggleTheme = () => {
    const newTheme = theme === 'light' ? 'dark' : 'light'
    setTheme(newTheme)
    document.documentElement.classList.toggle('dark', newTheme === 'dark')
    localStorage.setItem('theme', newTheme)
  }

  const handleLogout = async () => {
    // Capture where the user was BEFORE `signOut()` — once auth state
    // clears, `ProtectedRoute` may already have kicked us to `/login`
    // and `location` will point there. sessionStorage survives across
    // that transition; router `state.from` covers the SPA-navigation
    // case where React state isn't reset.
    const from = currentPathForReturn(location)
    setAuthReturnTo(from)
    await signOut()
    navigate('/login', { state: { from } })
    onClose()
  }

  // Get user info from context
  const userInfo = user || admin
  const displayName = userInfo?.full_name || userInfo?.email || 'User'
  const displayEmail = userInfo?.email || ''
  const initials = userInfo?.full_name
    ? userInfo.full_name.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)
    : (userInfo?.email?.[0] || 'U').toUpperCase()

  // Calculate dropdown position if it's a dropdown
  const getDropdownPosition = () => {
    if (!isDropdown || !dropdownRef?.current) return {}
    const rect = dropdownRef.current.getBoundingClientRect()
    const dropdownWidth = Math.min(260, window.innerWidth - 32)
    const left = Math.max(16, rect.right - dropdownWidth)
    const top = rect.bottom + 8
    
    return {
      position: 'fixed' as const,
      top: `${top}px`,
      left: `${left}px`,
      width: `${dropdownWidth}px`,
      zIndex: 300
    }
  }

  const menuItems = [
    { id: 'profile', label: 'Profile', icon: User },
    { id: 'settings', label: 'Settings', icon: Settings },
    { id: 'billing', label: 'Billing', icon: CreditCard },
    { id: 'theme', label: theme === 'light' ? 'Dark Mode' : 'Light Mode', icon: theme === 'light' ? Moon : Sun },
    { id: 'logout', label: 'Logout', icon: LogOut, danger: true },
  ]

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Modal/Dropdown */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95, y: isDropdown ? -10 : 20 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.95, y: isDropdown ? -10 : 20 }}
            className={isDropdown ? "z-[300]" : "fixed inset-0 z-50 flex items-center justify-center p-4"}
            style={isDropdown ? getDropdownPosition() : {}}
            onClick={(e) => !isDropdown && e.stopPropagation()}
          >
            <div className={`bg-white/95 dark:bg-slate-900/90 rounded-lg shadow-xl border border-gray-200 dark:border-slate-800 ${isDropdown ? 'max-h-[calc(100vh-5rem)]' : 'max-w-md w-full max-h-[90vh]'} overflow-y-auto backdrop-blur`}>
              {/* Header */}
              <div className="flex items-center justify-between p-4 border-b">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-primary-600 text-white flex items-center justify-center text-sm font-medium">
                    {initials}
                  </div>
                  <div>
                    <h2 className="text-sm font-bold text-gray-900 dark:text-white">Profile</h2>
                    <p className="text-xs text-gray-500 dark:text-gray-400">{displayName}</p>
                    {displayEmail && displayEmail !== displayName && (
                      <p className="text-xs text-gray-400 dark:text-gray-500">{displayEmail}</p>
                    )}
                    {role && (
                      <p className="text-xs text-primary-600 dark:text-primary-400 capitalize mt-0.5">
                        {role}
                      </p>
                    )}
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>

              {/* Content */}
              <div className="p-2">
                {menuItems.map((item) => {
                  const Icon = item.icon
                  return (
                    <button
                      key={item.id}
                      onClick={() => {
                        if (item.id === 'logout') {
                          handleLogout()
                        } else if (item.id === 'theme') {
                          toggleTheme()
                        } else {
                          onClose()
                        }
                      }}
                      className={`w-full flex items-center gap-3 p-3 rounded-lg transition-colors text-left ${
                        item.danger
                          ? 'text-red-600 hover:bg-red-100/40 dark:hover:bg-red-500/10'
                          : 'text-gray-700 dark:text-slate-200 hover:bg-gray-100 dark:hover:bg-slate-800/70'
                      }`}
                    >
                      <Icon className="h-5 w-5" />
                      <span className="text-sm font-medium">{item.label}</span>
                    </button>
                  )
                })}
              </div>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  )
}

